import { Drawer, Segmented, Typography, Space, Tag, theme as antdTheme } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { themeList, type SchoolTheme } from '@/themes';
import { useThemeStore } from '@/store/theme';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Combined theme + density picker. Replaces the legacy flat dropdown in the
 * header. Each theme card renders a mini-mockup in that theme's own colors so
 * the user can visually compare without applying first.
 *
 * Mockup pieces (kept inline so they inherit the previewed theme regardless
 * of what's currently applied to the app):
 *   - 28-px sider strip with brand mark + nav dots
 *   - title + subtitle in the theme's body font
 *   - button + tag rendered in primary / accent
 *   - faint hairline row to suggest table density
 */
export function AppearanceDrawer({ open, onClose }: Props) {
  const themeKey = useThemeStore((s) => s.themeKey);
  const setTheme = useThemeStore((s) => s.setTheme);
  const density = useThemeStore((s) => s.density);
  const setDensity = useThemeStore((s) => s.setDensity);
  const { token } = antdTheme.useToken();

  return (
    <Drawer
      title="Appearance"
      open={open}
      onClose={onClose}
      size={Math.min(560, typeof window !== 'undefined' ? window.innerWidth - 16 : 560)}
      destroyOnHidden
    >
      <Space orientation="vertical" size={20} style={{ width: '100%' }}>
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 11, letterSpacing: 0.8, textTransform: 'uppercase' }}>
            Density
          </Typography.Text>
          <div style={{ marginTop: 8 }}>
            <Segmented
              value={density}
              onChange={(v) => setDensity(v as 'comfortable' | 'compact')}
              options={[
                { label: 'Comfortable', value: 'comfortable' },
                { label: 'Compact', value: 'compact' },
              ]}
            />
          </div>
          <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 6, display: 'block' }}>
            Compact tightens table rows and form spacing. Independent of theme.
          </Typography.Text>
        </div>

        <div>
          <Typography.Text type="secondary" style={{ fontSize: 11, letterSpacing: 0.8, textTransform: 'uppercase' }}>
            Theme
          </Typography.Text>
          <div
            style={{
              marginTop: 10,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 12,
            }}
          >
            {themeList.map((t) => (
              <ThemeTile
                key={t.key}
                theme={t}
                selected={t.key === themeKey}
                onSelect={() => setTheme(t.key)}
                hostBorder={token.colorBorderSecondary}
              />
            ))}
          </div>
        </div>
      </Space>
    </Drawer>
  );
}

interface TileProps {
  theme: SchoolTheme;
  selected: boolean;
  onSelect: () => void;
  hostBorder: string;
}

/**
 * Mini-mockup of a theme. The crucial bit: colors come from the *previewed*
 * theme's config, NOT the currently-active token — so the user sees what
 * they'd get before clicking.
 */
function ThemeTile({ theme, selected, onSelect, hostBorder }: TileProps) {
  const t = theme.config.token ?? {};
  const layout = theme.config.components?.Layout ?? {};
  const primary = (t.colorPrimary as string) ?? '#5b9bff';
  const accent = theme.swatch[2];
  const bgLayout = (t.colorBgLayout as string) ?? '#fff';
  const bgContainer = (t.colorBgContainer as string) ?? '#fff';
  const border = (t.colorBorder as string) ?? '#e5e7eb';
  const borderSecondary = (t.colorBorderSecondary as string) ?? '#eef2f6';
  const textBase =
    (t.colorTextBase as string) ?? (theme.mode === 'dark' ? '#e5e7eb' : '#0f172a');
  const siderBg = (layout.siderBg as string) ?? '#0f172a';
  const radius = (t.borderRadius as number) ?? 8;
  const isPill = theme.shape === 'pill';

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      style={{
        appearance: 'none',
        textAlign: 'left',
        cursor: 'pointer',
        padding: 0,
        border: `1px solid ${selected ? primary : hostBorder}`,
        outline: selected ? `2px solid ${primary}` : 'none',
        outlineOffset: selected ? 2 : 0,
        borderRadius: 12,
        background: bgLayout,
        overflow: 'hidden',
        boxShadow: selected ? '0 6px 20px -10px rgba(0,0,0,0.25)' : 'none',
        transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
      }}
    >
      {/* Mock app shell */}
      <div style={{ display: 'flex', height: 100 }}>
        {/* Sider strip */}
        <div
          style={{
            width: 26,
            background: siderBg,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingTop: 8,
            gap: 6,
          }}
        >
          <span style={{ width: 12, height: 12, borderRadius: 4, background: primary, display: 'block' }} />
          <span style={{ width: 10, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.45)' }} />
          <span style={{ width: 10, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.22)' }} />
          <span style={{ width: 10, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.22)' }} />
        </div>
        {/* Main area */}
        <div style={{ flex: 1, padding: '10px 12px', background: bgLayout, color: textBase, minWidth: 0 }}>
          <div
            style={{
              fontFamily: theme.fontStack.heading,
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.2,
              color: textBase,
            }}
          >
            {theme.label}
          </div>
          <div
            style={{
              fontFamily: theme.fontStack.body,
              fontSize: 10,
              opacity: 0.7,
              marginTop: 2,
              color: textBase,
            }}
          >
            {theme.character}
          </div>

          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
            {/* Mock primary button */}
            <span
              style={{
                fontFamily: theme.fontStack.body,
                fontSize: 10,
                background: primary,
                color: theme.mode === 'dark' && primary.toLowerCase() === '#f59e0b' ? '#0a0a0a' : '#fff',
                padding: '3px 9px',
                borderRadius: isPill ? 999 : radius,
                fontWeight: 600,
              }}
            >
              Save
            </span>
            {/* Mock tag */}
            <span
              style={{
                fontFamily: theme.fontStack.body,
                fontSize: 9,
                background: bgContainer,
                color: accent,
                padding: '2px 6px',
                borderRadius: Math.max(radius - 2, 2),
                border: `1px solid ${border}`,
              }}
            >
              Active
            </span>
          </div>

          {/* Mock row hairlines (denote table density) */}
          <div style={{ marginTop: 10, display: 'grid', gap: 4 }}>
            <span style={{ height: 1, background: border, display: 'block' }} />
            <span style={{ height: 1, background: borderSecondary, display: 'block' }} />
            <span style={{ height: 1, background: borderSecondary, display: 'block' }} />
          </div>
        </div>
      </div>

      {/* Footer band — name + mode + selected check */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 10px',
          background: bgContainer,
          borderTop: `1px solid ${border}`,
          gap: 8,
        }}
      >
        <Typography.Text strong style={{ fontSize: 12, color: textBase }}>
          {theme.label}
        </Typography.Text>
        <Tag color={theme.mode === 'dark' ? 'default' : 'processing'} style={{ marginInlineStart: 0, fontSize: 10 }}>
          {theme.mode === 'dark' ? 'Dark' : 'Light'}
        </Tag>
        {selected && (
          <CheckCircleFilled style={{ marginLeft: 'auto', color: primary, fontSize: 16 }} />
        )}
      </div>
    </button>
  );
}
